import { showSuccess, showError } from '@calendly/alert'
import { useFlag } from '@calendly/feature-check-react'
import { I18n } from '@calendly/i18n'
import { createPortal } from 'react-dom'
import { useDispatch } from 'react-redux'

import { SettingsFormFooter } from 'components/core/form'
import {
  useUpdateUserMeetingIntelligenceDataMutation,
  useUpdateHubspotAccountForMiMutation,
  useUpdateSalesforceAccountForMiMutation,
} from 'features/meeting_intelligence/api'
import { useIntegrationsContext } from 'features/meeting_intelligence/components/recording_rules/integrations_provider'
import { userSettingsTrackingV3 } from 'features/meeting_intelligence/tracking'
import { useSettingsModalLayoutOptional } from 'features/settings_modal/layout_context'
import { useTrack } from 'hooks/analytics_events/useTrack'

import { DeleteFooter } from '../components/delete_footer'
import { useNotetakerSettingsContext } from '../notetaker_settings_provider'
import { useMeetingIntelligenceContext } from '../provider'
import { DeleteDisclaimerWrapper, DisclaimerButton, DisclaimerButtonWrapper } from './styles'

export const SubmitCancelSettings = ({
  setShowDeleteModal,
  handleTrackModal,
  adminDisabledNotetaker,
}) => {
  const settingsLayout = useSettingsModalLayoutOptional()
  const useEmbeddedSettingsFooter = settingsLayout?.showFooterHost === true
  const settingsFooterHost = settingsLayout?.footerHost
  const dispatch = useDispatch()
  const track = useTrack()
  const [update, { isLoading: isUpdateLoading }] = useUpdateUserMeetingIntelligenceDataMutation()
  const [updateHubspotAccount, { isLoading: isHubspotUpdateLoading }] = useUpdateHubspotAccountForMiMutation()
  const [updateSalesforceAccount, { isLoading: isSalesforceUpdateLoading }] = useUpdateSalesforceAccountForMiMutation()

  const isSaveLoading = isUpdateLoading || isHubspotUpdateLoading || isSalesforceUpdateLoading

  const {
    handleSubmit,
    handleSetValuesToOrigin,
  } = useMeetingIntelligenceContext()

  const {
    handleGatherIntegrations,
    handleCancelIntegrations,
    refetchIntegrations,
  } = useIntegrationsContext()

  const {
    handleGatherUserSettings,
    handleCancelUserSettings,
  } = useNotetakerSettingsContext()

  const integrationsCardEnabled = useFlag('integrations_card')

  const handleTrackingUpdate = (trackingValues) => {
    const trackingConfig = userSettingsTrackingV3.valuesSaved
    const trackingPayload = {
      name: trackingConfig.name,
      properties: {
        ...trackingConfig.props,
        edit_type: [JSON.stringify(trackingValues)]
      }
    }
    track(trackingPayload)
  }

  const handleUpdate = async (values, trackingValues) => {
    const { integrationsValues, userSettingsValues: notetakerSettingsValues } = values
    const promises = []

    // Always update MI settings
    promises.push(
      update({ ...notetakerSettingsValues, source: 'settings_page' }).unwrap().then(
        () => ({ type: 'mi', success: true }),
        (error) => ({ type: 'mi', success: false, error })
      )
    )

    // Conditionally add HubSpot/Salesforce updates only when integrations card is enabled
    if (integrationsCardEnabled) {
      const hubspotIntegration = integrationsValues?.find((integration) => integration.name === 'hubspot')
      if (hubspotIntegration) {
        promises.push(
          updateHubspotAccount({
            syncMiNotes: hubspotIntegration.enabled,
            syncEvents: hubspotIntegration.syncEvents,
            syncStatus: hubspotIntegration.syncStatus,
          }).unwrap().then(
            () => ({ type: 'hubspot', success: true }),
            (error) => ({ type: 'hubspot', success: false, error })
          )
        )
      }

      const salesforceIntegration = integrationsValues?.find((integration) => integration.name === 'salesforce')
      if (salesforceIntegration) {
        promises.push(
          updateSalesforceAccount({ meetingNotesEnabled: salesforceIntegration.enabled }).unwrap().then(
            () => ({ type: 'salesforce', success: true }),
            (error) => ({ type: 'salesforce', success: false, error })
          )
        )
      }
    }
    // Wait for all to complete
    const results = await Promise.allSettled(promises)

    // Check if any failed
    const hasFailures = results.some((result) => {
      if (result.status === 'fulfilled') {
        return !result.value.success
      }
      return true
    })

    // Show success or error toast
    if (hasFailures) {
      dispatch(showError(I18n.t('publisher.flash.error')))
    } else {
      dispatch(showSuccess(I18n.t('publisher.account_settings.meeting_intelligence.success')))
      if (integrationsCardEnabled) {
        await refetchIntegrations()
      }
      if (trackingValues) {
        handleTrackingUpdate(trackingValues)
      }
    }
  }

  const handleGatherValues = async () => {
    const contextValues = handleSubmit()
    const integrationsValues = handleGatherIntegrations()
    const { payload: userSettingsValues, valid } = handleGatherUserSettings()
    if (!valid) return
    const trackingValues = { ...contextValues, integrationsValues, userSettingsValues }

    await handleUpdate({ ...contextValues, integrationsValues, userSettingsValues }, trackingValues)
  }

  const handleCancel = () => {
    handleSetValuesToOrigin()
    handleCancelIntegrations()
    handleCancelUserSettings()
  }

  if (useEmbeddedSettingsFooter) {
    return settingsFooterHost != null && createPortal(
      <SettingsFormFooter
        leadingAction={(
          <DeleteFooter
            handleTrackModal={handleTrackModal}
            setShowDeleteModal={setShowDeleteModal}
          />
        )}
      >
        <DisclaimerButton
          onClick={handleCancel}
          appearance="secondary-outline"
        >
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.cancel')}
        </DisclaimerButton>
        <DisclaimerButton
          onClick={handleGatherValues}
          loading={isSaveLoading}
          disabled={adminDisabledNotetaker}
        >
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.save_changes')}
        </DisclaimerButton>
      </SettingsFormFooter>,
      settingsFooterHost,
    )
  }

  return (
    <DeleteDisclaimerWrapper data-testid="recaps-user-settings-delete">
      <DisclaimerButtonWrapper>
        <DisclaimerButton
          onClick={handleGatherValues}
          loading={isSaveLoading}
          disabled={adminDisabledNotetaker}
        >
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.save_changes')}
        </DisclaimerButton>
        <DisclaimerButton
          onClick={() => {
            handleCancel()
          }}
          appearance="secondary-outline"
        >
          {I18n.t('publisher.personal_settings.meeting_recaps.recording_rules.cancel')}
        </DisclaimerButton>
      </DisclaimerButtonWrapper>
      <DeleteFooter handleTrackModal={handleTrackModal} setShowDeleteModal={setShowDeleteModal} />
    </DeleteDisclaimerWrapper>
  )
}
